import React, { useMemo, useRef, useState } from 'react'
import { Button, Flex, Select } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { PeriodGroup } from '../../../types/periodGroup/PeriodGroup'
import { PeriodGroupSelection } from '../../../redux/context/filters/types'
import { yearQuarters, comparativePeriodGroup } from '../../../utils/periodGroups/utils'
import { useCtxFiscalYears } from '../../fiscalYear/queries/report/useCtxFiscalYears'
import PeriodTabFooter from './PeriodTabFooter'

type QuarterEntry = { id: number; year?: number; quarters: number[] }

interface QuartersTabProps {
  initialSelection?: PeriodGroupSelection & { type: 'quarters' }
  onSubmit: (periodGroups: PeriodGroup[], selection: PeriodGroupSelection) => void
  onClear: () => void
}

const QuartersTab: React.FC<QuartersTabProps> = ({ initialSelection, onSubmit, onClear }) => {
  const { t } = useTranslation()
  const { data: fiscalYears } = useCtxFiscalYears()
  const entryIdRef = useRef(0)

  const nextEntryId = () => {
    entryIdRef.current += 1
    return entryIdRef.current
  }

  const initialEntries: QuarterEntry[] = initialSelection?.entries?.length
    ? initialSelection.entries.map(e => ({
        id: nextEntryId(),
        year: e.year,
        quarters: e.quarters
      }))
    : [{ id: nextEntryId(), year: undefined, quarters: [] }]

  const [entries, setEntries] = useState<QuarterEntry[]>(initialEntries)

  const yearOptions = useMemo(
    () =>
      fiscalYears
        ?.map(fy => ({
          label: fy.year,
          value: fy.year
        }))
        .reverse() ?? [],
    [fiscalYears]
  )

  const updateYear = (id: number, year: number) => {
    setEntries(prev => prev.map(e => (e.id === id ? { ...e, year, quarters: [] } : e)))
  }

  const toggleQuarter = (id: number, q: number) => {
    setEntries(prev =>
      prev.map(e =>
        e.id === id
          ? { ...e, quarters: e.quarters.includes(q) ? e.quarters.filter(v => v !== q) : [...e.quarters, q] }
          : e
      )
    )
  }

  const addEntry = () => {
    setEntries(prev => [...prev, { id: nextEntryId(), year: undefined, quarters: [] }])
  }

  const removeEntry = (id: number) => {
    setEntries(prev => prev.filter(e => e.id !== id))
  }

  const handleSubmit = () => {
    const validEntries = entries.filter(e => e.year !== undefined && e.quarters.length > 0) as {
      id: number
      year: number
      quarters: number[]
    }[]
    if (validEntries.length === 0) return

    const allPeriodGroups: PeriodGroup[] = []
    for (const entry of validEntries) {
      const allQuarters = yearQuarters(entry.year)
      const filtered = allQuarters.filter((_, i) => entry.quarters.includes(i + 1))
      const comparativeQuarters = filtered.map(comparativePeriodGroup)
      allPeriodGroups.push(...comparativeQuarters, ...filtered)
    }

    onSubmit(allPeriodGroups, {
      type: 'quarters',
      entries: validEntries.map(({ year, quarters }) => ({ year, quarters }))
    })
  }

  return (
    <div>
      <Flex vertical gap={8}>
        {entries.map(entry => (
          <Flex key={entry.id} gap={4} align="center">
            <Flex gap={8} style={{ flex: 1 }}>
              <Select
                style={{ flex: 1 }}
                options={yearOptions}
                value={entry.year}
                onChange={v => updateYear(entry.id, v)}
                placeholder={t('periodSelect:select-year')}
              />
              {[1, 2, 3, 4].map(q => (
                <Button
                  key={q}
                  type={entry.quarters.includes(q) ? 'primary' : 'default'}
                  disabled={entry.year === undefined}
                  onClick={() => toggleQuarter(entry.id, q)}
                >
                  Q{q}
                </Button>
              ))}
            </Flex>
            {entries.length > 1 && (
              <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => removeEntry(entry.id)} />
            )}
          </Flex>
        ))}
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={addEntry}>
          {t('global:add')}
        </Button>
      </Flex>
      <PeriodTabFooter onSubmit={handleSubmit} onClear={onClear} />
    </div>
  )
}

export default QuartersTab
